import React from 'react';
import { KeyboardArrowRight, ExpandMore, HistoryOutlined, Warning } from '@mui/icons-material';
import {
  checkNAOrReturnPercentValue,
  ExpandableText,
  formatTaxPercentValue,
  getDateFormat,
} from '../utils/common-methods';
import { formatCurrencyValue } from '../utils/common-methods';
import { Tooltip, Chip, Typography, Box } from '@mui/material';
import ChangesCell from '../components/Table/ChangesCell';
import SublocationsPopoverCell from '../pages/WeeklyGLPosting/components/SublocationsPopoverCell';
import { MONTHLY_RECONCILIATION_VARIANCE_COLOR } from '../pages/Collection/MonthlyReconciliation/constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MapConfidenceTooltipContent from '../pages/ExceptionReporting/MapConfidenceTooltipContent';
import { Link } from '../components/shared';
import { routeConstants } from './routeConstants';
import FIELD_TYPE from './fieldType';
import { MESSAGE } from './message';
import dayjs from 'dayjs';
import IncludedSublocationsCell from '../pages/Collection/MonthlyReconciliation/IncludeSublocationCell';

const { NOT_APPLICABLE } = FIELD_TYPE;
const stopIndicatorInteraction = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const formatDateCell = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MM/DD/YYYY') : '—';
};

export const COLUMNS = {
  LOCATION_TABLE: {
    COMMON: [
      {
        Header: 'LOCATION NAME',
        accessor: 'locationName',
        width: 'auto',
      },
      {
        Header: 'LOCATION TYPE',
        accessor: 'locationType',
        width: 'auto',
      },
      { Header: 'PRODUCT TYPE', accessor: 'productTypes', hideHeaderAndColumn: false, width: 'auto' },
    ],
    get ACTIVE() {
      return [
        ...this.COMMON,
        {
          Header: 'CITY',
          accessor: 'cityName',
          width: 'auto',
        },
      ];
    },

    get ARCHIVE() {
      return [
        ...this.COMMON,
        {
          Header: 'DATE ARCHIVED',
          accessor: 'dateArchived',
          width: 'auto',
        },
        {
          Header: () => null,
          id: 'archive',
          Cell: () => (
            <span data-testid="expandIcon">
              <HistoryOutlined />
            </span>
          ),
        },
      ];
    },
  },
  SUBLOCATION_TABLE_DEFAULT: [
    {
      Header: 'Zip Code',
      accessor: 'code',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'Phone 1',
      accessor: 'phone1',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'Phone 2',
      accessor: 'phone2',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'Open Hours',
      accessor: 'openHours',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'Close Hours',
      accessor: 'closeHours',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'Start Date',
      accessor: 'startDate',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'End Date',
      accessor: 'endDate',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'currency',
      accessor: 'currency',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'sub_location_type',
      accessor: 'subLocationType',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'address 1',
      accessor: 'address1',
      hideHeaderAndColumn: true,
    },
    {
      Header: 'address 2',
      accessor: 'address2',
      hideHeaderAndColumn: true,
    },
  ],
  SUBLOCATION_MACHINE_TABLE: [
    {
      Header: 'MACHINE NAME',
      accessor: 'title',
    },
    {
      Header: 'MACHINE TYPE',
      accessor: ' machineType',
    },
  ],
  USER_TABLE: [
    {
      Header: 'NAME',
      accessor: 'name',
    },
    {
      Header: 'EMAIL ID',
      accessor: 'email',
    },
    {
      Header: 'ROLE',
      accessor: 'roles',
    },
    {
      Header: 'LOCATION',
      accessor: 'location',
    },
  ],
  ASSIGN_LOCATION_TABLE: [
    {
      Header: 'Address',
      accessor: 'address',
    },
    {
      Header: 'City',
      accessor: 'city',
    },
    {
      Header: 'State',
      accessor: 'state',
    },
    {
      Header: 'Postal',
      accessor: 'postcode',
    },
  ],
  COLLECTIONS_LOCATIONS_TABLE: [
    {
      Header: 'LOCATION NAME',
      accessor: 'locationName',
      width: 'auto',
    },
    {
      Header: 'SUB LOCATIONS NAME',
      accessor: 'sublocationName',
      width: 'auto',
    },
    { Header: 'PRODUCT TYPE', accessor: 'productTypes', width: 'auto' },
    {
      Header: 'LAST COLLECTION',
      accessor: 'lastCollection',
      width: 'auto',
    },
  ],
  SUBMITTED_COLLECTIONS_LOCATIONS_TABLE: [
    {
      Header: 'LOCATION NAME',
      accessor: 'locationName',
      sortKey: 'locationName',
      width: 'auto',
    },
    {
      Header: 'SUBMITTED BY',
      accessor: 'serviceRep',
      sortKey: 'firstName',
      width: 'auto',
    },
    {
      Header: 'TYPE',
      accessor: 'locationType',
      width: 'auto',
    },
    {
      Header: 'INDEPENDENT REP',
      accessor: 'independentRep',
      width: 'auto',
    },
    { Header: 'PRODUCT TYPE', accessor: 'productTypes', width: 'auto' },
    {
      Header: 'SUBMISSION DATE',
      accessor: 'submissionDate',
      sortKey: 'dateEntered',
      width: 'auto',
    },
    {
      Header: 'TOTAL REVENUE',
      accessor: 'locationRevenue',
      width: 'auto',
    },
  ],
  SUBMITTED_COLLECTIONS_FOR_SUBLOCATIONS_TABLE: [
    {
      Header: 'LOCATION NAME',
      accessor: 'locationName',
      width: 'auto',
    },
    {
      Header: 'SUBMITTED BY',
      accessor: 'submittedByType',
      width: 'auto',
    },
    { Header: 'PRODUCT TYPE', accessor: 'productTypes', width: 'auto' },
    {
      Header: 'INDEPENDENT REP',
      accessor: 'independentRep',
      width: 'auto',
    },
    {
      Header: 'TOTAL REVENUE',
      accessor: 'totalRevenue',
      width: 'auto',
    },
  ],
  SUBMITTED_COLLECTIONS_FOR_LOCATIONS_TABLE: [
    {
      Header: 'LOCATION NAME',
      accessor: 'locationName',
      width: 'auto',
    },
    {
      Header: 'SUBMITTED BY',
      accessor: 'submittedByType',
      width: 'auto',
    },
    { Header: 'PRODUCT TYPE', accessor: 'productTypes', width: 'auto' },
    {
      Header: 'INDEPENDENT REP',
      accessor: 'independentRep',
      width: 'auto',
    },
    {
      Header: 'TOTAL REVENUE',
      accessor: 'totalRevenue',
      width: 'auto',
    },
  ],
  APPROVED_COLLECTION_TABLE: [
    {
      Header: 'LOCATION NAME',
      accessor: 'locationName',
      sortKey: 'locationName',
      width: 'auto',
    },
    {
      Header: 'SUBMITTED BY',
      accessor: 'serviceRep',
      sortKey: 'firstName',
      width: 'auto',
    },
    {
      Header: 'TYPE',
      accessor: 'locationType',
      width: 'auto',
    },
    {
      Header: 'INDEPENDENT REP',
      accessor: 'independentRep',
      width: 'auto',
    },
    { Header: 'PRODUCT TYPE', accessor: 'productTypes', width: 'auto' },
    {
      Header: 'SUBMISSION DATE',
      accessor: 'submissionDate',
      sortKey: 'dateEntered',
      width: 'auto',
    },
    {
      Header: 'TOTAL REVENUE',
      accessor: 'locationRevenue',
      width: 'auto',
    },
  ],
  LOCATION_OWNERS_TABLE: [
    {
      Header: 'Owner / Business Name',
      accessor: 'name',
    },
    {
      Header: 'City',
      accessor: 'city',
    },
    {
      Header: 'State',
      accessor: 'state',
    },
    {
      Header: 'Postal Code',
      accessor: 'postal_code',
    },
  ],
  MODEMS_TABLE: [
    {
      Header: 'Serial Number',
      accessor: 'serialNumber',
      width: 'auto',
    },
    {
      Header: 'Installed date',
      accessor: 'installedDate',
      width: 'auto',
    },
    {
      Header: 'Type',
      accessor: 'type',
      width: 'auto',
    },
    {
      Header: 'Asset ID',
      accessor: (row) => row.modemHistories?.assetId || NOT_APPLICABLE,
      width: 'auto',
    },
    {
      Header: 'In Effect From',
      accessor: (row) => getDateFormat(row.modemHistories?.inEffectFrom),
      width: 'auto',
    },
  ],

  MODEM_HISTORY_COLUMNS: [
    {
      Header: 'Serial Number',
      accessor: 'serialNumber',
      width: 'auto',
    },
    {
      Header: 'Type',
      accessor: 'type',
      width: 'auto',
    },
    {
      Header: 'Asset Name',
      accessor: 'assetName',
      width: 'auto',
    },
    {
      Header: 'Changes',
      accessor: 'changes',
      width: 'auto',
    },
    {
      Header: 'In Effect From',
      accessor: 'inEffectFrom',
      width: 'auto',
    },
    {
      Header: 'In Effect To',
      accessor: 'inEffectTo',
      width: 'auto',
    },
    {
      Header: 'Updated By',
      accessor: 'updatedBy',
      width: 'auto',
    },
    {
      Header: 'Updated At',
      accessor: 'updatedAt',
      width: 'auto',
      Cell: ({ value }) => <span style={{ textTransform: 'none' }}>{value}</span>,
    },
  ],

  ASSIGN_MODEM_TABLE: [
    {
      Header: 'SERIAL NUMBER',
      accessor: 'serialNumber',
      width: 'auto',
    },
    {
      Header: 'INSTALLED DATE',
      accessor: 'installedDate',
      width: 'auto',
    },
    {
      Header: 'TYPE',
      accessor: 'type',
      width: 'auto',
    },
    {
      Header: 'MACHINE NAME',
      accessor: (row) => row?.modemHistories?.asset?.title,
      width: 'auto',
    },
  ],
  MACHINE_MODEM_DETAILS_TABLE: [
    {
      Header: 'TYPE',
      accessor: 'type',
      width: 'auto',
    },
    {
      Header: 'SERIAL NUMBER',
      accessor: 'serialNumber',
      width: 'auto',
    },
    {
      Header: 'IN EFFECT FROM',
      accessor: 'inEffectFrom',
      width: 'auto',
    },
  ],
  ASSET_METER_TABLE: [
    {
      Header: 'POSITION',
      accessor: 'identifier',
      width: 'auto',
    },
    {
      Header: 'MAX COUNT',
      accessor: 'maxCount',
      width: 'auto',
    },
    {
      Header: 'CURRENT METER',
      accessor: 'currentAssetMeterReading',
      width: 'auto',
    },
    {
      Header: 'DISPLAY NAME',
      accessor: 'displayName',
      width: 'auto',
    },
    {
      Header: 'COIN COST',
      accessor: 'coinCost',
      width: 'auto',
    },
  ],
  ASSIGN_MACHINE_TABLE: [
    {
      Header: 'ASSET ID',
      accessor: 'id',
      width: 'auto',
    },
    {
      Header: 'LEGACY ASSET ID',
      accessor: (row) => {
        const { asset } = row || {};
        const legacyAssetId = asset?.legacyAssetId || row.legacyAssetId;

        return legacyAssetId;
      },
      width: 'auto',
    },
    {
      Header: 'MODEL NO',
      accessor: (row) => {
        const { asset } = row || {};
        const modelId = asset?.modelId || row.modelId;
        return modelId;
      },
      width: 'auto',
    },
    {
      Header: 'MACHINE NAME',
      accessor: (row) => row.title,
      width: 'auto',
      Cell: ({ row }) => {
        const { asset } = row?.original || {};
        const machineId = asset?.id || row.original?.id;
        const machineTitle = asset?.title || row.original?.title;
        if (!machineId) {
          return machineTitle || 'N/A';
        }

        return (
          <Link underline="none" to={`/${routeConstants.MACHINES_ROUTE}/${machineId}`}>
            {machineTitle || 'N/A'}
          </Link>
        );
      },
    },
    {
      Header: 'MODEL TYPE',
      accessor: (row) => {
        const { asset } = row || {};
        const modelTitle = asset?.model?.title || row.modelTitle;
        return modelTitle;
      },
      width: 'auto',
    },
    {
      Header: 'SUBLOCATION NAME',
      accessor: (row) => {
        const { sublocation } = row || {};
        const sublocationName = sublocation?.name || row?.assetHistories?.[0]?.sublocation?.name;
        return sublocationName;
      },
      width: 'auto',
    },
  ],
  ASSIGN_SUBLOCATION_MACHINE_TABLE: [
    {
      Header: 'MODEL TYPE',
      accessor: 'modelTitle',
      width: 'auto',
    },
    {
      Header: 'SUBLOCATION NAME',
      accessor: (row) => row?.assetHistories?.[0]?.sublocation?.name,
      width: 'auto',
    },
  ],
  INTERCARD_S3_LOCATION_TABLE: [
    {
      Header: 'INTERCARD CATEGORY',
      accessor: 'intercardCategory',
      width: 'auto',
    },
    {
      Header: 'REVENUE TYPE',
      accessor: 'revenueType',
      width: 'auto',
    },
    {
      Header: 'REVENUE DATE',
      accessor: 'revenueDate',
      width: 'auto',
    },
    {
      Header: 'UPLOADED AT',
      accessor: 'uploadedAt',
      width: 'auto',
    },
  ],
  EXPAND_ICON_COLUMN: {
    Header: () => null,
    id: 'expander',
    isPadding: true,
    padding: '20px 0px 20px 24px',
    width: 56,
    Cell: ({ row }) => (
      <span
        {...row.getToggleRowExpandedProps()}
        data-testid="expandIcon"
        style={{
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {row.isExpanded ? <ExpandMore /> : <KeyboardArrowRight />}
      </span>
    ),
  },
  RECONCILIATION_ACCORDION_ASSET_TABLE: [
    {
      Header: 'ASSET ID',
      accessor: 'id',
      width: 'auto',
    },
    {
      Header: 'LEGACY ASSET ID',
      accessor: 'legacyAssetId',
      width: 'auto',
    },
    {
      Header: 'MACHINE NAME',
      accessor: 'title',
      width: 'auto',
    },
  ],
  ASSET_WARNING_COLUMN: {
    Header: () => null,
    id: 'assetWarning',
    Cell: ({ row }) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        {row?.original?.hasWarning ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label={MESSAGE.VARIANCE_FOUND_TOOLTIP}
            onClick={stopIndicatorInteraction}
            onMouseDown={stopIndicatorInteraction}
            style={{
              display: 'inline-flex',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'default',
            }}
          >
            <Tooltip
              title={MESSAGE.VARIANCE_FOUND_TOOLTIP}
              placement="top-end"
              enterDelay={50}
              leaveDelay={150}
              enterNextDelay={0}
              disableInteractive
              componentsProps={{
                popper: {
                  sx: {
                    '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                      marginBottom: '2px !important',
                    },
                  },
                },
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    color: '#111827',
                    border: '1px solid #b9c7d8',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  },
                },
              }}
            >
              <Warning data-testid="asset-warning-icon" color="error" fontSize="small" />
            </Tooltip>
          </button>
        ) : null}
      </span>
    ),
    width: 56,
  },
  CREATE_COLLECTION_ASSET_TABLE: [
    {
      Header: 'LAST COLLECTION',
      accessor: 'lastCollection',
      width: 'auto',
    },
  ],
  RECONCILIATION_APPROVED_ACCORDION_ASSET_TABLE: [
    {
      Header: 'CLICKS PER PLAY',
      accessor: 'clicksPerPlay',
      width: 'auto',
    },
    {
      Header: 'COST PER PLAY',
      accessor: 'costPerPlay',
      width: 'auto',
    },
  ],

  AREA_TAX_HISTORY: [
    {
      Header: 'In Effect From',
      accessor: 'inEffectFrom',
      width: 'auto',
    },
    {
      Header: 'In Effect To',
      accessor: 'inEffectTo',
      width: 'auto',
    },
    {
      Header: 'Area Tax',
      accessor: 'areaTax',
      width: 'auto',
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      width: 'auto',
    },
  ],

  STATE_TAX_HISTORY: [
    {
      Header: 'In Effect From',
      accessor: 'inEffectFrom',
      width: 'auto',
    },
    {
      Header: 'In Effect To',
      accessor: 'inEffectTo',
      width: 'auto',
    },
    {
      Header: 'State',
      accessor: 'state',
      width: 'auto',
    },
    {
      Header: 'State Tax',
      accessor: 'stateTax',
      width: 'auto',
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      width: 'auto',
    },
  ],

  INDEPENDENT_REP: [
    {
      Header: 'INDEPENDENT REP',
      accessor: 'independantRep',
      width: 'auto',
    },
    {
      Header: 'SUBLOCATION NAME',
      accessor: 'sublocationName',
      width: 'auto',
    },
    {
      Header: 'Pay',
      accessor: 'pay',
      width: 'auto',
    },
  ],
  INDEPENDANT_REP_PAY_HISTORY: [
    {
      Header: 'Status',
      accessor: 'indicator',
      width: 'auto',
    },
    {
      Header: 'In Effect From',
      accessor: 'inEffectFrom',
      width: 'auto',
    },
    {
      Header: 'In Effect To',
      accessor: 'inEffectTo',
      width: 'auto',
    },
    {
      Header: 'Rep',
      accessor: 'rep',
      width: 'auto',
    },
    {
      Header: 'Type',
      accessor: 'type',
      width: 'auto',
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      width: 'auto',
    },
    {
      Header: 'Fee',
      accessor: 'fee',
      width: 'auto',
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      width: 'auto',
    },
  ],
};

const LOCATION_TABLE_DYNAMIC = (isBelowMD, isArchiveTab) => {
  const COMMON = [
    {
      Header: 'STATE',
      accessor: 'stateName',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
  ];
  const ACTIVE_COLUMNS = [
    ...COMMON,
    {
      Header: 'COUNTRY',
      accessor: 'countryName',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
  ];
  const ARCHIVE_COLUMNS = [
    {
      Header: 'CITY',
      accessor: 'cityName',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
    ...COMMON,
  ];
  return isArchiveTab ? ARCHIVE_COLUMNS : ACTIVE_COLUMNS;
};

const SUBLOCATION_TABLE_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'LEGACY SUB LOCATION ID',
      accessor: 'legacysublocId',
      width: 'auto',
    },
    {
      Header: 'NO.OF MACHINES',
      accessor: 'noOfMachines',
      width: 'auto',
      hideHeaderAndColumn: isBelowMD,
    },
  ];
  return columns;
};

const USER_TABLE_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'NAME',
      accessor: 'name',
    },
    {
      Header: 'EMAIL ID',
      accessor: 'email',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'ROLE',
      accessor: 'roles',
    },
    {
      Header: 'SUB LOCATION',
      accessor: 'location',
    },
  ];
  return columns;
};

const USER_ASSIGN_LOCATION_TABLE_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'City',
      accessor: 'city',
      width: 300,
    },
    {
      Header: 'State',
      accessor: 'state',
      width: 300,
    },
    {
      Header: 'Postal',
      accessor: 'postcode',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
  ];

  return columns;
};

const MODEL_TABLE_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'MODEL TITLE',
      accessor: 'modelTitle',
      width: 'auto',
    },
    {
      Header: 'MANUFACTURER',
      accessor: 'manufacturer',
      width: 'auto',
    },
    {
      Header: 'THEME',
      accessor: 'theme',
      width: 'auto',
    },
    {
      Header: 'SUB THEME',
      accessor: 'subTheme',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
    {
      Header: 'CATEGORY',
      accessor: 'category',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
  ];

  return columns;
};

const MODEL_MACHINE_DYNAMIC_COLUMN = (isBelowMD) => [
  {
    Header: 'MACHINE ID',
    accessor: 'machineId',
    width: 'auto',
  },
  {
    Header: 'TITLE',
    accessor: 'title',
    width: 'auto',
  },
  {
    Header: 'STATUS',
    accessor: 'statusTitle',
    width: 'auto',
  },
  {
    Header: 'NUMBER OF PLAYS',
    accessor: 'numberOfPlays',
    hideHeaderAndColumn: isBelowMD,
    width: 'auto',
  },
  {
    Header: 'INSTALLED DATE',
    accessor: 'installedDate',
    hideHeaderAndColumn: isBelowMD,
    width: 'auto',
  },
  {
    Header: 'SUB LOCATION',
    accessor: 'sublocationName',
    width: 'auto',
  },
];

const USER_DETAILS_DYNAMIC_COLUMNS = (isBelowMD) => {
  const columns = [
    {
      Header: 'ADDRESS',
      accessor: 'address1',
      width: 'auto',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'ZIPCODE',
      accessor: 'postcode',
      width: 'auto',
      hideHeaderAndColumn: isBelowMD,
    },
  ];
  return columns;
};

const USER_DETAILS_DYNAMIC_SUB_LOC_TABLE = (isBelowMD) => {
  // This is a Table within Table in user details page of location's sub-location
  // So we need to use react to re-render the table, because react-table package does not re-render it when there is a change
  // Hence the below code
  if (isBelowMD)
    return [
      {
        Header: 'SUB LOCATION NAME',
        accessor: 'subLocationName',
        width: 'auto',
      },
      {
        Header: 'STATE',
        accessor: 'state',
        width: 'auto',
      },
      {
        Header: 'ZIPCODE',
        accessor: 'postcode',
        width: 'auto',
      },
      {
        Header: 'REP RATE %',
        accessor: 'repRate',
        width: 'auto',
        Cell: (props) => checkNAOrReturnPercentValue(props?.value),
        hideHeaderAndColumn: isBelowMD,
      },
      {
        Header: 'REP FEE',
        accessor: 'repFee',
        width: 'auto',
        hideHeaderAndColumn: isBelowMD,
      },
      {
        Header: 'TYPE',
        accessor: 'type',
        width: 'auto',
        hideHeaderAndColumn: isBelowMD,
      },
      {
        Header: 'GL NUMBER',
        accessor: 'accountNumber',
        width: 'auto',
        hideHeaderAndColumn: isBelowMD,
      },
    ];

  return [
    {
      Header: 'SUB LOCATION NAME ',
      accessor: 'subLocationName',
      width: 'auto',
    },

    {
      Header: 'CITY',
      accessor: 'city',
      width: 'auto',
    },
    {
      Header: 'STATE',
      accessor: 'state',
      width: 'auto',
    },
    {
      Header: 'ZIPCODE',
      accessor: 'postcode',
      width: 'auto',
    },
    {
      Header: 'REP RATE %',
      accessor: 'repRate',
      width: 'auto',
      Cell: (props) => checkNAOrReturnPercentValue(props?.value),
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'REP FEE',
      accessor: 'repFee',
      width: 'auto',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'TYPE',
      accessor: 'type',
      width: 'auto',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'GL NUMBER',
      accessor: 'accountNumber',
      width: 'auto',
      hideHeaderAndColumn: isBelowMD,
    },
  ];
};
const MACHINE_TABLE_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'ASSET ID',
      accessor: 'id',
      width: 'auto',
    },
    {
      Header: 'LEGACY ASSET ID',
      accessor: 'legacyAssetId',
      width: 'auto',
    },
    {
      Header: 'MACHINE NAME',
      accessor: 'title',
      width: 'auto',
    },
    {
      Header: 'STATUS',
      accessor: 'statusTitle',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
    {
      Header: 'MODEL NAME',
      accessor: 'modelTitle',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
    {
      Header: 'SUBLOCATION',
      accessor: 'sublocationName',
      width: 'auto',
    },
  ];
  return columns;
};

const DEPOSIT_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'DEPOSIT #',
      accessor: 'id',
    },
    {
      Header: 'SERVICE REP',
      accessor: 'serviceRep',
    },
    {
      Header: 'SUB LOCATION NAME',
      accessor: 'sublocationName',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'DEPOSIT DATE',
      accessor: 'depositDate',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'TOTAL DEPOSIT AMOUNT',
      accessor: 'totalDepositAmount',
    },
  ];

  return columns;
};

const LEASE_TABLE_DYNAMIC = (isArchiveTab, expandedRows, toggleExpandRow) => {
  const COMMON = [
    {
      Header: 'LEASE NAME',
      accessor: 'name',
      width: isArchiveTab ? 900 : 600,
    },
    {
      Header: 'SUB LOCATION',
      accessor: 'sublocation',
      width: 600,
      Cell: ({ row }) => {
        // Safely get sublocations
        const sublocations = row.original.sublocation || [];

        // Extract names safely
        const names = sublocations?.map((sub) => sub?.sublocation?.name).filter((name) => name && name.trim() !== '');

        // If no valid names found, return N/A
        if (names.length === 0) return 'N/A';

        // Return sorted, comma-separated list
        return (
          <ExpandableText
            text={names.sort().join(', ')}
            expanded={expandedRows[row.id] || false}
            toggleExpand={() => toggleExpandRow(row.id)}
          />
        );
      },
    },
    {
      Header: 'CONTRACT LINK',
      accessor: 'contractLink',
      width: 400,
      Cell: ({ value }) => {
        if (!value || value === null || value === 'N/A') {
          return 'N/A';
        }

        let isNavigableLink = false;
        try {
          const url = new URL(value);
          isNavigableLink = url.protocol === 'http:' || url.protocol === 'https:';
        } catch (e) {
          isNavigableLink = false;
        }

        const sharedLinkStyle = {
          textDecoration: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
          whiteSpace: 'nowrap',
          maxWidth: '330px',
          cursor: isNavigableLink ? 'pointer' : 'default',
          color: isNavigableLink ? 'blue' : 'inherit',
        };

        const tooltipProps = {
          tooltip: {
            sx: {
              bgcolor: 'white',
              color: 'black',
              border: '1px solid #e0e0e0',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
              fontSize: '12px',
              p: 1,
            },
          },
        };

        return (
          <Tooltip title={value} componentsProps={tooltipProps}>
            {isNavigableLink ? (
              <a href={value} target="_blank" rel="noopener noreferrer" style={sharedLinkStyle}>
                {value}
              </a>
            ) : (
              <span style={sharedLinkStyle}>{value}</span>
            )}
          </Tooltip>
        );
      },
    },
    {
      Header: 'START DATE',
      accessor: 'startDate',
      width: 'auto',
    },
    {
      Header: 'END DATE',
      accessor: 'endDate',
      width: 'auto',
    },
  ];
  const ACTIVE_COLUMNS = [...COMMON];

  const ARCHIVE_COLUMNS = [
    {
      Header: 'ARCHIVED DATE',
      accessor: 'archivedDate',
      width: 150,
    },
    ...COMMON,
  ];

  return isArchiveTab ? ARCHIVE_COLUMNS : ACTIVE_COLUMNS;
};

const CURRENCY_EXCHANGE_DYNAMIC = (isBelowMD) => {
  const columns = [
    {
      Header: 'CURRENCY CODE',
      accessor: 'currencyCode',
      width: 'auto',
    },
    {
      Header: 'CURRENCY NAME',
      accessor: 'currencyName',
      width: 'auto',
      Cell: ({ row }) => {
        const exchangeRateId = row?.original?.id || row?.original?.exchangeRateId;
        const currencyName = row?.original?.currencyName || 'N/A';

        if (!exchangeRateId) {
          return currencyName;
        }

        return (
          <Link
            underline="none"
            to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.CURRENCY_RATES_EXCHANGE_ROUTE}/${exchangeRateId}`}
          >
            {currencyName}
          </Link>
        );
      },
    },
    {
      Header: 'EXCHANGE RATE',
      accessor: 'exchangeRate',
      width: 'auto',
      Cell: ({ value }) => (value ? parseFloat(value).toFixed(4) : 'N/A'),
    },
    {
      Header: 'BASE CURRENCY',
      accessor: 'baseCurrency',
      width: 'auto',
      Cell: () => 'USD',
    },
    {
      Header: 'EXCHANGE RATE DATE',
      accessor: 'rateDate',
      hideHeaderAndColumn: isBelowMD,
      width: 'auto',
    },
    {
      Header: 'PROVIDER',
      accessor: 'provider',
      width: 'auto',
    },
    {
      Header: 'UPDATED BY',
      accessor: 'updatedByName',
      width: 'auto',
      Cell: ({ value }) => value || '—',
    },
  ];

  return columns;
};

const INTERCARD_TRACKINGS_TABLE = () => {
  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      width: 'auto',
    },
    {
      Header: 'JOB ID',
      accessor: 'job_id',
      width: 'auto',
    },
    {
      Header: 'JOB TYPE',
      accessor: 'job_type',
      width: 'auto',
    },
    {
      Header: 'STATUS',
      accessor: 'status',
      width: 'auto',
    },
    {
      Header: 'PROCESS STATUS',
      accessor: 'process_status',
      width: 'auto',
    },
    {
      Header: 'REVENUE DATE',
      accessor: 'revenueDate',
      width: 'auto',
    },
    {
      Header: 'CREATED DATE',
      accessor: 'createdAt',
      width: 'auto',
    },
    {
      Header: 'CREATED BY',
      accessor: 'createdBy',
      width: 'auto',
    },
  ];

  return columns;
};

const UPDATE_STATE_TAX_RATE_TABLE = () => {
  const columns = [
    {
      Header: 'Country Name',
      accessor: 'country.name',
      width: 'auto',
    },
    {
      Header: 'State Name',
      accessor: 'name',
      width: 'auto',
    },
    {
      Header: 'State Taxrate %',
      accessor: 'stateTaxRate',
      width: 'auto',
      Cell: ({ value }) => formatTaxPercentValue(value),
    },
    {
      Header: 'Updated BY',
      accessor: 'updatedByUser',
      width: 'auto',
    },
  ];
  return columns;
};

const MANAGE_AREA_TAX_LOCATION_TABLE = () => {
  const columns = [
    {
      Header: 'Area Tax Location Name',
      accessor: 'areaTaxLocationName',
      width: '240',
    },
    {
      Header: 'SUBLOCATIONS',
      accessor: 'sublocations',
      width: '400',
    },
    {
      Header: 'Tax Rate %',
      accessor: 'activeAreaTax',
      width: '120',
      Cell: ({ value }) => formatTaxPercentValue(value),
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: '140',
      Cell: ({ value }) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      Header: 'Updated By',
      accessor: 'updatedBy',
      width: '180',
      Cell: ({ value }) => {
        if (!value) return 'N/A';
        return `${value.firstName} ${value.lastName}`;
      },
    },
  ];
  return columns;
};

const SEEDLIVE_TRACKINGS_TABLE = () => {
  const columns = [
    COLUMNS.EXPAND_ICON_COLUMN,
    {
      Header: 'ID',
      accessor: 'id',
      width: 'auto',
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: 'auto',
      Cell: ({ value }) => (value === true ? 'Completed' : 'Failed'),
    },
    {
      Header: 'PROCESS STATUS',
      accessor: 'process_status',
      width: 'auto',
    },
    {
      Header: 'CREATED DATE',
      accessor: 'createdAt',
      width: 'auto',
    },
  ];

  return columns;
};

const NAYAX_TRACKINGS_TABLE = () => {
  const columns = [
    {
      Header: 'JOB ID',
      accessor: 'job_id',
      width: '450',
    },
    {
      Header: 'STATUS',
      accessor: 'status',
      width: 'auto',
    },
    {
      Header: 'PROCESS STATUS',
      accessor: 'process_status',
      width: 'auto',
    },
    {
      Header: 'QUEUE START TIME',
      accessor: 'queue_start_time_formatted',
      width: 'auto',
    },
    {
      Header: 'QUEUE FINISH TIME',
      accessor: 'queue_finish_time_formatted',
      width: 'auto',
    },
    {
      Header: 'CREATED AT',
      accessor: 'created_at_formatted',
      width: 'auto',
    },
  ];

  return columns;
};

const VENDOR_TABLE_DYNAMIC = (isBelowMD, expandedRows, toggleExpandRow) => {
  const columns = [
    {
      Header: 'VENDOR NAME',
      Cell: ({ row }) => row.original.customerNameElement,
    },
    {
      Header: 'VENDOR CODE',
      accessor: 'vendorCode',
      hideHeaderAndColumn: isBelowMD,
    },
    {
      Header: 'EMAILS',
      accessor: 'emails',
      Cell: ({ row }) => (
        <ExpandableText
          text={row.original.emails}
          expanded={expandedRows[row.id] || false}
          toggleExpand={() => toggleExpandRow(row.id)}
        />
      ),
    },
    {
      Header: 'SUB LOCATION',
      accessor: 'sublocations',
      Cell: ({ row }) => (
        <ExpandableText
          text={
            row.original.sublocations
              ?.map((sub) => sub.name)
              .filter(Boolean)
              .sort()
              .join(', ') || 'N/A'
          }
          expanded={expandedRows[row.id] || false}
          toggleExpand={() => toggleExpandRow(row.id)}
        />
      ),
    },
    {
      Header: 'STATUS',
      accessor: 'status',
    },
  ];
  return columns;
};

export const LEASE_HISTORY_COLUMNS = [
  {
    Header: 'Lease/Contract Name',
    accessor: 'leaseName',
    width: 'auto',
  },
  {
    Header: 'Sublocation',
    accessor: 'sublocationName',
    width: 'auto',
  },
  {
    Header: 'In Effect From',
    accessor: 'inEffectFrom',
    width: 'auto',
  },
  {
    Header: 'In Effect To',
    accessor: 'inEffectTo',
    width: 'auto',
  },
  {
    Header: 'Summary',
    accessor: 'summary',
    width: 'auto',
    Cell: ({ value }) => {
      if (!value || value === 'N/A') return 'N/A';
      return <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{value}</div>;
    },
  },
  {
    Header: 'Changes',
    accessor: 'changes',
    width: 400,
    Cell: ({ value }) => <ChangesCell value={value} />,
  },
  {
    Header: 'Modified By',
    accessor: 'modifiedBy',
    width: 'auto',
  },
  {
    Header: 'Modified At',
    accessor: 'modifiedAt',
    width: 'auto',
    Cell: ({ value }) => <span style={{ textTransform: 'none' }}>{value}</span>,
  },
];
/**
 * Converts weighted column widths into percentages that always add up to 100%, so a
 * fixed-layout table shows every column on screen without horizontal scrolling.
 * @param {Array} columns - Column definitions whose `width` is a relative weight
 * @returns {Array} The same columns with percentage widths
 */
const withPercentageWidths = (columns) => {
  const totalWeight = columns.reduce((total, column) => total + column.width, 0);

  return columns.map((column) => ({
    ...column,
    width: `${((column.width / totalWeight) * 100).toFixed(2)}%`,
  }));
};

const UNMAPPED_DATA_FEED = () => {
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';
  const columns = [
    {
      Header: 'SOURCE',
      accessor: 'source',
      width: 9,
      canSort: true,
      id: 'source',
    },
    {
      Header: 'ROWS AFFECTED',
      accessor: 'rows_affected',
      width: 7,
      canSort: true,
      id: 'rows_affected',
    },
    {
      Header: 'AMOUNT AFFECTED',
      accessor: 'amount_affected',
      width: 8,
      canSort: true,
      id: 'amount_affected',
    },
    {
      Header: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          MAP CONFIDENCE
          <Tooltip
            title={<MapConfidenceTooltipContent />}
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                },
              },
            }}
          >
            <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
      ),
      accessor: 'map_confidence',
      width: 12,
      canSort: true,
      id: 'map_confidence',
    },
    {
      Header: 'UNMAPPED SCENARIO',
      accessor: 'unmapped_scenario',
      width: 11,
      canSort: true,
      id: 'unmapped_scenario',
    },
    {
      Header: 'DATE AFFECTED',
      accessor: 'date_range',
      width: 10,
      canSort: true,
      id: 'date_affected_from',
    },
    {
      Header: 'SUBLOCATION',
      accessor: 'sublocation',
      width: 8,
      canSort: true,
      id: 'sublocation',
    },
    {
      Header: 'ASSET',
      accessor: 'asset',
      width: 7,
      canSort: true,
      id: 'asset',
    },
    {
      Header: 'MODEM',
      accessor: 'modem',
      width: 9,
      canSort: true,
      id: 'modem',
    },
    isResyncEnabled && {
      Header: 'ALERT',
      accessor: 'alert',
      width: 22,
      canSort: false,
      id: 'alert',
      Cell: ({ value }) => {
        if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
          return '-';
        }

        const getRoute = (label, data) => {
          switch (label) {
            case 'Modem':
              return data?.id ? `/${routeConstants.MODEMS_ROUTE}/${data.id}` : null;
            case 'Asset':
              return data?.id ? `/${routeConstants.MACHINES_ROUTE}/${data.id}` : null;
            case 'Sublocation':
              return data?.id ? `/${routeConstants.SUBLOCATION_ROUTE}/${data.id}` : null;
            default:
              return null;
          }
        };

        const getDisplayId = (label, data) => {
          switch (label) {
            case 'Modem':
              return data?.serialNumber || data?.id;
            case 'Asset':
              return data?.id;
            case 'Sublocation':
              return data?.id;
            default:
              return data?.id;
          }
        };

        const items = [
          { label: 'Modem', data: value.modem },
          { label: 'Asset', data: value.asset },
          { label: 'Sublocation', data: value.sublocation },
        ].filter((item) => item.data !== undefined);

        if (items.length === 0) {
          return '-';
        }

        const unmappedItems = items.filter((item) => !item.data?.mapped);
        const mappedItems = items.filter((item) => item.data?.mapped);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {unmappedItems.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {unmappedItems.map((item) => {
                  const idValue = getDisplayId(item.label, item.data);
                  return (
                    <div
                      key={item.label}
                      style={{
                        padding: '4px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ef9a9a',
                        fontSize: '11px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#c62828',
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontWeight: 600, color: '#c62828' }}>{item.label}</span>
                        <span style={{ color: '#c62828', fontWeight: 500, marginLeft: 'auto' }}>Unmapped</span>
                      </div>
                      {idValue && (
                        <div
                          style={{ color: '#757575', fontSize: '10px', overflowWrap: 'anywhere', paddingLeft: '14px' }}
                        >
                          ID: {idValue}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {mappedItems.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {mappedItems.map((item) => {
                  const route = getRoute(item.label, item.data);
                  const displayId = getDisplayId(item.label, item.data);
                  const content = (
                    <>
                      <span style={{ fontSize: '10px' }}>✓</span>
                      <span style={{ overflowWrap: 'anywhere' }}>
                        {item.label}: {displayId}
                      </span>
                    </>
                  );

                  if (route) {
                    return (
                      <Link
                        key={item.label}
                        to={route}
                        underline="none"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textDecoration: 'none',
                        }}
                      >
                        {content}
                      </Link>
                    );
                  }

                  return (
                    <span
                      key={item.label}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontSize: '11px',
                      }}
                    >
                      {content}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean);

  return withPercentageWidths(columns);
};

const MISSING_DATA_FEED = () => {
  const columns = [
    {
      Header: 'DATE',
      accessor: 'date',
      width: 9,
      canSort: true,
      id: 'date',
    },
    {
      Header: 'SOURCE NAME',
      accessor: 'source_name',
      width: 12,
      canSort: true,
      id: 'source_name',
    },
    {
      Header: 'ISSUE',
      accessor: 'issue',
      width: 13,
      canSort: true,
      id: 'issue',
    },
    {
      Header: 'JOB ID',
      accessor: 'job_id',
      width: 9,
      canSort: true,
      id: 'job_id',
    },
    {
      Header: 'JOB TYPE',
      accessor: 'job_type',
      width: 10,
      canSort: true,
      id: 'job_type',
    },
    {
      Header: 'STATUS',
      accessor: 'status',
      width: 9,
      canSort: true,
      id: 'status',
    },
    {
      Header: 'TRK STAGING',
      accessor: 'trk_staging',
      width: 10,
      canSort: true,
      id: 'trk_staging',
    },
    {
      Header: 'NUM ROWS STAGING',
      accessor: 'num_rows_staging',
      width: 9,
      canSort: true,
      id: 'num_rows_staging',
    },
    {
      Header: 'NUM ROWS MAIN',
      accessor: 'num_rows_main',
      width: 9,
      canSort: true,
      id: 'num_rows_main',
    },
    {
      Header: 'UNIQUE SUBLOCATIONS',
      accessor: 'unique_sublocations',
      width: 10,
      canSort: true,
      id: 'unique_sublocations',
    },
  ];

  return withPercentageWidths(columns);
};

const MONTHLY_OPEN_TABLE = [
  {
    Header: 'Product Type',
    accessor: 'productType',
    id: 'productType',
    width: 200,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{value}</Typography>,
  },
  {
    Header: 'Calendar Type',
    accessor: 'calendarType',
    id: 'calendarType',
    width: 150,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{value}</Typography>,
  },
  {
    Header: 'Approver Name',
    accessor: 'approverName',
    id: 'approverName',
    width: 200,
    canSort: true,
    Cell: ({ value }) => (
      <Typography variant="body2" sx={{ color: value === 'Pending' ? 'text.secondary' : 'text.primary' }}>
        {value}
      </Typography>
    ),
  },
  {
    Header: 'Last Approved Month',
    accessor: 'lastApprovedMonth',
    id: 'lastApprovedMonth',
    width: 180,
    canSort: true,
    Cell: ({ value }) => (
      <Typography variant="body2" sx={{ color: value === 'Pending' ? 'text.secondary' : 'text.primary' }}>
        {value}
      </Typography>
    ),
  },
];

const MONTHLY_FINALIZED_TABLE = [
  {
    Header: 'Monthly Posting ID',
    accessor: 'postingId',
    id: 'postingId',
    width: 220,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{value ?? '—'}</Typography>,
  },
  {
    Header: 'X3 Product Name',
    accessor: 'productType',
    id: 'productType',
    width: 160,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{value ?? '—'}</Typography>,
  },
  {
    Header: 'Start Date',
    accessor: 'startDate',
    id: 'startDate',
    width: 120,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{formatDateCell(value)}</Typography>,
  },
  {
    Header: 'End Date',
    accessor: 'endDate',
    id: 'endDate',
    width: 120,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{formatDateCell(value)}</Typography>,
  },
  {
    Header: 'Posting Date',
    accessor: 'postingDate',
    id: 'postingDate',
    width: 140,
    canSort: false,
    Cell: ({ value }) => <Typography variant="body2">{formatDateCell(value)}</Typography>,
  },
  {
    Header: 'Finalized By',
    accessor: 'approverName',
    id: 'approverName',
    width: 180,
    canSort: true,
    Cell: ({ value }) => <Typography variant="body2">{value ?? '—'}</Typography>,
  },
];

const MonthlyDetailCurrencyCell = ({ value, row }) => (
  <Typography
    variant="body2"
    textAlign="left"
    color="secondary.main"
    fontWeight={row.original?.isSummaryRow || row.original?.isAggregateRow ? 600 : 400}
  >
    {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
  </Typography>
);

const MonthlyDetailVarianceCell = ({ value }) => {
  const rawValue = value ?? 0;
  const normalizedValue = Math.abs(rawValue) < 0.005 ? 0 : rawValue;
  return (
    <Typography variant="body2" textAlign="left" sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }} fontWeight={600}>
      {formatCurrencyValue(normalizedValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </Typography>
  );
};

const MonthlyDetailTextCell = ({ value, row }) => (
  <Typography
    variant="body2"
    color="secondary.main"
    textAlign="left"
    fontWeight={row.original?.isSummaryRow || row.original?.isAggregateRow ? 600 : 400}
  >
    {value ?? '—'}
  </Typography>
);

const MONTHLY_POSTING_DETAIL_TABLE = [
  {
    Header: 'Sublocations',
    accessor: 'isIncluded',
    width: 120,
    Cell: IncludedSublocationsCell,
  },
  { Header: 'Date Range', accessor: 'dateRange', width: 180, Cell: MonthlyDetailTextCell },
  { Header: 'Posting Date', accessor: 'postingDate', width: 140, Cell: MonthlyDetailTextCell },
  { Header: 'Posting ID', accessor: 'postingId', width: 220, Cell: MonthlyDetailTextCell },
  { Header: 'CC Current', accessor: 'creditCardCurrent', width: 120, Cell: MonthlyDetailCurrencyCell },
  { Header: 'CC Posted', accessor: 'creditCardPosted', width: 120, Cell: MonthlyDetailCurrencyCell },
  { Header: 'Credit Variance', accessor: 'creditVariance', width: 130, Cell: MonthlyDetailVarianceCell },
  { Header: 'Cash Current', accessor: 'cashCurrent', width: 120, Cell: MonthlyDetailCurrencyCell },
  { Header: 'Cash Posted', accessor: 'cashPosted', width: 120, Cell: MonthlyDetailCurrencyCell },
  { Header: 'Cash Variance', accessor: 'cashVariance', width: 130, Cell: MonthlyDetailVarianceCell },
  { Header: 'Total Current', accessor: 'totalCurrent', width: 130, Cell: MonthlyDetailCurrencyCell },
  { Header: 'Total Variance', accessor: 'totalVariance', width: 130, Cell: MonthlyDetailVarianceCell },
];

const MONTHLY_RECONCILIATION_WEEKLY_POSTINGS = () => {
  const columns = [
    {
      Header: 'Sublocations ',
      accessor: 'isIncluded',
      width: 120,
      Cell: IncludedSublocationsCell,
    },
    {
      Header: 'Date Range',
      accessor: 'weekLabel',
      width: 90,
      Cell: ({ value, row }) => {
        if (row.original?.isSummaryRow) return null;

        return (
          <Typography variant="body2" color="secondary.main" textAlign="left" noWrap>
            {value ?? '—'}
          </Typography>
        );
      },
    },
    {
      Header: 'Posting Date',
      accessor: 'postingDate',
      width: 110,
      Cell: ({ value, row }) => {
        if (row.original?.isSummaryRow) return null;

        return (
          <Typography variant="body2" color="secondary.main" textAlign="left" noWrap>
            {value ?? '—'}
          </Typography>
        );
      },
    },
    {
      Header: 'Posting ID',
      accessor: 'postingId',
      width: 120,
      Cell: ({ value, row }) => (
        <Typography
          variant="body2"
          color="secondary.main"
          textAlign="left"
          fontWeight={row.original?.isSummaryRow ? 600 : 400}
        >
          {value}
        </Typography>
      ),
    },
    {
      Header: 'CC Current',
      accessor: 'creditCardCurrent',
      width: 150,
      Cell: ({ row, value }) => {
        const { Typography } = require('@mui/material');
        if (row.original?.isSummaryRow) {
          if (row.original?.summaryType === 'currentMonth' || row.original?.summaryType === 'total') {
            return (
              <Typography variant="body2" fontWeight={600} color="secondary.main" textAlign="left">
                {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            );
          }
          return null;
        }

        return (
          <Typography variant="body2" color="secondary.main" textAlign="left">
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'CC Posted',
      accessor: 'creditCardPosted',
      width: 150,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        if (row.original?.isSummaryRow) {
          if (row.original?.summaryType === 'totalPosted' || row.original?.summaryType === 'total') {
            return (
              <Typography variant="body2" fontWeight={600} color="secondary.main" textAlign="left">
                {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            );
          }
          return null;
        }

        return (
          <Typography variant="body2" color="secondary.main" textAlign="left">
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Credit Variance',
      accessor: 'creditVariance',
      width: 130,
      Cell: ({ row }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        if (row.original?.summaryType === 'variance' || row.original?.summaryType === 'total') {
          const rawVariance = row.original.creditVariance ?? 0;
          const variance = Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;
          return (
            <Typography
              variant="body2"
              fontWeight={600}
              textAlign="left"
              sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }}
            >
              {formatCurrencyValue(variance, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          );
        }

        if (row.original?.isSummaryRow) return null;

        const rawVariance = row.original.creditVariance ?? 0;
        const variance = Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign="left"
            sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }}
          >
            {formatCurrencyValue(variance, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Cash Current',
      accessor: 'cashCurrent',
      width: 120,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        return (
          <Typography
            variant="body2"
            color="secondary.main"
            textAlign="left"
            fontWeight={row.original?.isSummaryRow ? 600 : 400}
          >
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Cash Posted',
      accessor: 'cashPosted',
      width: 120,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        return (
          <Typography
            variant="body2"
            color="secondary.main"
            textAlign="left"
            fontWeight={row.original?.isSummaryRow ? 600 : 400}
          >
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Cash Variance',
      accessor: 'cashVariance',
      width: 130,
      Cell: ({ row }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        if (row.original?.summaryType === 'variance' || row.original?.summaryType === 'total') {
          const rawVariance = row.original.cashVariance ?? 0;
          const variance = Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;
          return (
            <Typography
              variant="body2"
              fontWeight={600}
              textAlign="left"
              sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }}
            >
              {formatCurrencyValue(variance, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          );
        }

        if (row.original?.isSummaryRow) return null;

        const rawVariance = row.original.cashVariance ?? 0;
        const variance = Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign="left"
            sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }}
          >
            {formatCurrencyValue(variance, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Refund Current',
      accessor: 'refundCurrent',
      width: 130,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        const refundValue = value ?? 0;
        const displayValue = refundValue ? -Math.abs(refundValue) : 0;

        return (
          <Typography
            variant="body2"
            color="secondary.main"
            textAlign="left"
            fontWeight={row.original?.isSummaryRow ? 600 : 400}
          >
            {formatCurrencyValue(displayValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Total Current',
      accessor: 'totalCurrent',
      width: 150,
      Cell: ({ row }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        if (row.original?.isSummaryRow) {
          if (row.original?.summaryType === 'currentMonth' || row.original?.summaryType === 'total') {
            return (
              <Typography variant="body2" fontWeight={600} color="secondary.main" textAlign="left">
                {formatCurrencyValue(row.original.totalCurrent ?? 0, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            );
          }
          return null;
        }

        // For regular rows: adjust Total Current by variance and refunds
        const totalCurrent =
          (row.original.creditCardCurrent ?? 0) +
          (row.original.cashCurrent ?? 0) -
          (row.original.totalVariance ?? 0) -
          (row.original.refundCurrent ?? 0);

        return (
          <Typography variant="body2" color="secondary.main" textAlign="left">
            {formatCurrencyValue(totalCurrent, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Total Variance',
      accessor: 'totalVariance',
      width: 150,
      Cell: ({ row }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');

        if (row.original?.summaryType === 'variance' || row.original?.summaryType === 'total') {
          const rawVariance = row.original.totalVariance ?? 0;
          const variance = Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;
          return (
            <Typography
              variant="body2"
              fontWeight={600}
              textAlign="left"
              sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }}
            >
              {formatCurrencyValue(variance, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          );
        }

        if (row.original?.isSummaryRow) return null;

        // Display total variance from backend
        const rawVariance = row.original.totalVariance ?? 0;
        const variance = Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign="left"
            sx={{ color: MONTHLY_RECONCILIATION_VARIANCE_COLOR }}
          >
            {formatCurrencyValue(variance, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
  ];

  return columns;
};

const MONTHLY_RECONCILIATION_SUBLOCATION_TABLE = () => {
  const { formatCurrencyValue } = require('../utils/common-methods');
  const { Typography } = require('@mui/material');
  const columns = [
    {
      Header: 'Sub Location',
      accessor: 'sublocation',
      width: 180,
      Cell: ({ row, value }) => (
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            fontWeight: row.original?.isSummaryRow || row.original?.isBalancingEntry ? 700 : 400,
            fontStyle: row.original?.isBalancingEntry ? 'italic' : 'normal',
          }}
        >
          {value || '—'}
        </Typography>
      ),
    },
    {
      Header: 'Date Affected',
      accessor: 'dateAffected',
      width: 180,
      Cell: ({ row, value }) => (
        <Typography variant="body2" textAlign="center" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
          {value || '—'}
        </Typography>
      ),
    },
    {
      Header: 'Asset ID',
      accessor: 'assetId',
      width: 100,
      Cell: ({ row, value }) => (
        <Typography variant="body2" textAlign="center" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
          {value || '—'}
        </Typography>
      ),
    },
    {
      Header: 'CC Current',
      accessor: 'creditCardCurrent',
      width: 120,
      Cell: ({ row, value }) => (
        <Typography variant="body2" textAlign="right" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
          {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      Header: 'CC Posted',
      accessor: 'creditCardPosted',
      width: 120,
      Cell: ({ row, value }) => (
        <Typography variant="body2" textAlign="right" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
          {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      Header: 'Credit Variance',
      accessor: 'creditVariance',
      width: 130,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');
        const rawValue = value ?? 0;
        const normalizedValue = Math.abs(rawValue) < 0.005 ? 0 : rawValue;
        return (
          <Typography
            variant="body2"
            textAlign="right"
            sx={{
              color: MONTHLY_RECONCILIATION_VARIANCE_COLOR,
              fontWeight: row.original?.isSummaryRow ? 700 : 600,
            }}
          >
            {formatCurrencyValue(normalizedValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Cash Current',
      accessor: 'cashCurrent',
      width: 120,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');
        return (
          <Typography variant="body2" textAlign="right" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Cash Posted',
      accessor: 'cashPosted',
      width: 120,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');
        return (
          <Typography variant="body2" textAlign="right" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Cash Variance',
      accessor: 'cashVariance',
      width: 130,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');
        const rawValue = value ?? 0;
        const normalizedValue = Math.abs(rawValue) < 0.005 ? 0 : rawValue;
        return (
          <Typography
            variant="body2"
            textAlign="right"
            sx={{
              color: MONTHLY_RECONCILIATION_VARIANCE_COLOR,
              fontWeight: row.original?.isSummaryRow ? 700 : 600,
            }}
          >
            {formatCurrencyValue(normalizedValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Total Current',
      accessor: 'totalCurrent',
      width: 130,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');
        return (
          <Typography variant="body2" textAlign="right" sx={{ fontWeight: row.original?.isSummaryRow ? 700 : 400 }}>
            {formatCurrencyValue(value ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      Header: 'Total Variance',
      accessor: 'totalVariance',
      width: 130,
      Cell: ({ row, value }) => {
        const { formatCurrencyValue } = require('../utils/common-methods');
        const { Typography } = require('@mui/material');
        const rawValue = value ?? 0;
        const normalizedValue = Math.abs(rawValue) < 0.005 ? 0 : rawValue;
        return (
          <Typography
            variant="body2"
            textAlign="right"
            sx={{
              color: MONTHLY_RECONCILIATION_VARIANCE_COLOR,
              fontWeight: row.original?.isSummaryRow ? 700 : 600,
            }}
          >
            {formatCurrencyValue(normalizedValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
  ];

  return columns;
};

const APPROVED_GL_POSTING_COLUMNS = [
  {
    Header: 'POSTING ID',
    accessor: 'postingId',
    width: 'auto',
    canSort: false,
  },
  {
    Header: 'DATE RANGE',
    accessor: 'postedWeek',
    width: 'auto',
    canSort: true,
  },
  {
    Header: 'X3 PRODUCT NAME',
    accessor: 'productType',
    width: 'auto',
    canSort: false,
  },
  {
    Header: 'SUBLOCATIONS',
    accessor: 'sublocations',
    width: 'auto',
    canSort: false,
    Cell: ({ value, row }) => {
      const selectedSublocations = Array.isArray(row?.original?.selectedSublocations)
        ? row.original.selectedSublocations
        : Array.isArray(value)
        ? value
        : [];
      const unselectedSublocations = Array.isArray(row?.original?.unselectedSublocations)
        ? row.original.unselectedSublocations
        : [];

      return (
        <SublocationsPopoverCell
          sublocations={selectedSublocations}
          selectedSublocations={selectedSublocations}
          unselectedSublocations={unselectedSublocations}
          x3ProductName={row?.original?.x3ProductName || row?.original?.productType}
        />
      );
    },
  },
  {
    Header: 'CREATED BY',
    accessor: 'createdBy',
    width: 'auto',
    canSort: false,
  },
  {
    Header: 'POSTING DATE',
    accessor: 'postingDate',
    width: 'auto',
    canSort: false,
  },
];

// Weekly GL Posting - Feed Source Data Panel Columns
const FEED_SOURCE_DATA_COLUMNS = [
  {
    Header: 'Feed Source',
    accessor: 'feedSource',
    width: 'auto',
  },
  {
    Header: 'Cash',
    accessor: 'cash',
    width: 'auto',
  },
  {
    Header: 'Credit',
    accessor: 'credit',
    width: 'auto',
  },
  {
    Header: 'Refund',
    accessor: 'refund',
    width: 'auto',
  },
  {
    Header: 'Total',
    accessor: 'total',
    width: 'auto',
  },
];

export {
  LOCATION_TABLE_DYNAMIC,
  SUBLOCATION_TABLE_DYNAMIC,
  USER_TABLE_DYNAMIC,
  USER_ASSIGN_LOCATION_TABLE_DYNAMIC,
  MODEL_TABLE_DYNAMIC,
  MODEL_MACHINE_DYNAMIC_COLUMN,
  USER_DETAILS_DYNAMIC_COLUMNS,
  USER_DETAILS_DYNAMIC_SUB_LOC_TABLE,
  MACHINE_TABLE_DYNAMIC,
  DEPOSIT_DYNAMIC,
  LEASE_TABLE_DYNAMIC,
  CURRENCY_EXCHANGE_DYNAMIC,
  INTERCARD_TRACKINGS_TABLE,
  UPDATE_STATE_TAX_RATE_TABLE,
  MANAGE_AREA_TAX_LOCATION_TABLE,
  SEEDLIVE_TRACKINGS_TABLE,
  VENDOR_TABLE_DYNAMIC,
  UNMAPPED_DATA_FEED,
  MISSING_DATA_FEED,
  NAYAX_TRACKINGS_TABLE,
  MONTHLY_OPEN_TABLE,
  MONTHLY_FINALIZED_TABLE,
  MONTHLY_POSTING_DETAIL_TABLE,
  MONTHLY_RECONCILIATION_WEEKLY_POSTINGS,
  MONTHLY_RECONCILIATION_SUBLOCATION_TABLE,
  APPROVED_GL_POSTING_COLUMNS,
  FEED_SOURCE_DATA_COLUMNS,
};
